import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { buildRiskPatternTitle } from '../../domain/safetyCases/riskPatternPortfolio';
import {
  buildCaseTimelineEvents,
  buildMeasurementSummary,
  formatTimelineTimestamp,
} from '../../domain/safetyCases/workspaceCaseDetailPanels';
import {
  collectTechnicalIds,
  friendlyDisplayLabel,
  looksLikeRawTechnicalId,
} from '../../domain/safetyCases/workspaceFriendlyLabels';
import type { SafetyDashboardRow } from '../../services/reportsApi';
import {
  RiskPatternDispositionPanel,
  type LinkableSafetyCaseOption,
  type RiskPatternDispositionKind,
} from './RiskPatternDispositionPanel';

function formatWhen(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function countLabel(count: number | null | undefined, singular: string, plural: string): string | null {
  if (typeof count !== 'number' || !Number.isFinite(count)) return null;
  return `${count} ${count === 1 ? singular : plural}`;
}

function resolveOwner(row: SafetyDashboardRow): string {
  if (row.has_followup_owner === false) return 'Unassigned';
  const owner = String(row.followup_owner || row.owner_name || row.owner || '').trim();
  const team = String(row.followup_team || '').trim();
  if (team && owner && team.toLowerCase() !== owner.toLowerCase()) return `${team} / ${owner}`;
  return owner || team || 'Unassigned';
}

function resolveZone(row: SafetyDashboardRow): string {
  const friendly = (
    friendlyDisplayLabel(row.zone_label, row.zone_id)
    || friendlyDisplayLabel(row.area_label, row.zone_id)
  );
  if (friendly) return friendly;
  const zoneId = String(row.zone_id || '').trim();
  if (zoneId && !looksLikeRawTechnicalId(zoneId)) {
    return zoneId.replace(/[_-]+/g, ' ');
  }
  return String(row.area_label || row.site_label || row.site_id || 'Location not recorded').trim();
}

function coverageSummary(row: SafetyDashboardRow): string | null {
  const attention = String(row.customer_attention_work_state || '').trim();
  const scanState = String(row.customer_scan_workflow_state || '').trim();
  if (attention === 'Insufficient coverage' || scanState === 'Insufficient coverage') {
    return 'Observation coverage was insufficient for the intended conclusion.';
  }
  const impact = String(row.impact_status_label || row.impact_measurement_status || '').trim();
  if (/insufficient/i.test(impact)) {
    return impact;
  }
  const blocker = (row.blockers || []).find((entry) => {
    const code = String(entry.code || '').toLowerCase();
    const message = String(entry.message || '').trim();
    return Boolean(message) && (code.includes('coverage') || /coverage/i.test(message));
  });
  if (blocker?.message) return String(blocker.message).trim();
  return null;
}

function MetaCell({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function RiskPatternAttentionDrawer({
  open,
  row,
  onClose,
  onReviewEvidence,
  canDispose = false,
  dispositionBusy = false,
  dispositionError = null,
  linkableCases = [],
  actorUser = null,
  onDisposition,
}: {
  open: boolean;
  row: SafetyDashboardRow | null;
  onClose: () => void;
  onReviewEvidence: () => void;
  canDispose?: boolean;
  dispositionBusy?: boolean;
  dispositionError?: string | null;
  linkableCases?: LinkableSafetyCaseOption[];
  actorUser?: { username?: string | null; email?: string | null } | null;
  onDisposition?: (
    kind: RiskPatternDispositionKind,
    payload: {
      create?: {
        title: string;
        owner: string;
        unassigned: boolean;
        rationale: string;
        confirmed: boolean;
      };
      notActionable?: { reasonCategory: string; reviewerNote: string };
      needsEvidence?: { reason: string; minimumEvidence: string; reviewDate: string };
      linkExisting?: { safetyCaseId: string; rationale: string };
    },
  ) => void;
}) {
  const active = open && row ? row : null;
  const title = active ? buildRiskPatternTitle(active) : '';
  const workState = active
    ? (String(
      active.customer_risk_pattern_work_state
      || active.customer_attention_work_state
      || active.review_status_label
      || active.review_status
      || 'Review required',
    ).trim() || 'Review required')
    : '';
  const observationLabel = countLabel(active?.observation_count, 'qualifying observation', 'qualifying observations');
  const evidenceLabel = countLabel(
    active?.representative_evidence_count,
    'reviewable episode',
    'reviewable episodes',
  );
  const cameraLabel = active ? friendlyDisplayLabel(active.camera_label, active.camera_id) : null;
  const siteLabel = String(active?.site_label || active?.site_id || '—').trim() || '—';
  const zoneLabel = active ? resolveZone(active) : '—';
  const owner = active ? resolveOwner(active) : 'Unassigned';
  const coverage = active ? coverageSummary(active) : null;
  const technicalIds = active
    ? collectTechnicalIds({
      cameraId: active.camera_id,
      cameraLabel: active.camera_label,
      deviceId: active.device_id,
      deviceLabel: active.device_label,
      zoneId: active.zone_id,
      zoneLabel: active.zone_label || active.area_label,
      workflowId: active.workflow_id,
    })
    : [];
  const guidanceBits = active
    ? ([
      active.scan_template_label ? `Related Scan template: ${active.scan_template_label}` : null,
      Array.isArray(active.control_review_focus) && active.control_review_focus.length > 0
        ? `Control review focus: ${active.control_review_focus.join(', ')}`
        : null,
    ].filter(Boolean) as string[])
    : [];
  const methodologyBits = active
    ? ([
      active.claim_boundary_profile_label
        ? `Claim boundary: ${active.claim_boundary_profile_label}`
        : null,
      active.hazard_exposure_label ? `Hazard exposure: ${active.hazard_exposure_label}` : null,
      active.risk_family_label ? `Risk family: ${active.risk_family_label}` : null,
    ].filter(Boolean) as string[])
    : [];

  const evidenceLines = [
    observationLabel,
    evidenceLabel,
    coverage,
  ].filter(Boolean) as string[];
  const timelineEvents = active ? buildCaseTimelineEvents(active) : [];
  const measurement = active ? buildMeasurementSummary(active) : null;

  return (
    <Drawer
      anchor="right"
      open={Boolean(active)}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560, md: 640 },
          maxWidth: '100%',
          p: 2.5,
          bgcolor: '#f8fafc',
        },
      }}
    >
      {active ? (
        <Stack spacing={2.5} sx={{ height: '100%' }} data-testid="risk-pattern-attention-drawer">
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                Risk Pattern
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }} data-testid="risk-pattern-drawer-title">
                {title}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip size="small" label={workState} data-testid="risk-pattern-drawer-work-state" />
                {active.customer_status_label ? (
                  <Chip size="small" variant="outlined" label={String(active.customer_status_label)} />
                ) : null}
              </Stack>
            </Box>
            <IconButton aria-label="Close Risk Pattern drawer" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            data-testid="risk-pattern-drawer-header-meta"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 1.25,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <MetaCell label="Owner" value={owner} />
            <MetaCell label="Site" value={siteLabel} />
            <MetaCell label="Area / zone" value={zoneLabel} />
            <MetaCell label="Camera" value={cameraLabel || 'Not recorded'} />
            <MetaCell label="First detected" value={formatWhen(active.first_detected_at)} />
            <MetaCell
              label="Latest observation"
              value={formatWhen(active.latest_observation_at || active.updated_at)}
            />
          </Box>

          <Box
            data-testid="risk-pattern-drawer-evidence"
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Evidence summary
            </Typography>
            {evidenceLines.length > 0 ? (
              <Stack spacing={0.75}>
                {evidenceLines.map((line) => (
                  <Typography key={line} variant="body2">
                    {line}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Qualifying observation counts are not available on this summary. Open full review to inspect representative evidence.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
              Representative media and episode playback stay in the full Risk Pattern review until this drawer loads evidence attachments.
            </Typography>
          </Box>

          <Box
            data-testid="risk-pattern-drawer-decision"
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light',
              bgcolor: 'background.paper',
            }}
          >
            <RiskPatternDispositionPanel
              row={active}
              canDispose={canDispose}
              busy={dispositionBusy}
              linkableCases={linkableCases}
              actorUser={actorUser}
              error={dispositionError}
              onReviewEvidence={onReviewEvidence}
              onSubmit={(kind, payload) => onDisposition?.(kind, payload)}
            />
          </Box>

          <Box
            data-testid="risk-pattern-drawer-timeline"
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Case timeline
            </Typography>
            {timelineEvents.length > 0 ? (
              <Stack spacing={1.25} component="ol" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
                {timelineEvents.map((event) => (
                  <Box
                    key={event.id}
                    component="li"
                    data-testid={`risk-pattern-timeline-event-${event.id}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(7.5rem, 8.5rem) 1fr',
                      gap: 1,
                      alignItems: 'start',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ pt: 0.15 }}>
                      {formatTimelineTimestamp(event.timestamp)}
                    </Typography>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {event.action}
                      </Typography>
                      <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                        {event.actor ? (
                          <Typography variant="caption" color="text.secondary">
                            Actor: {event.actor}
                          </Typography>
                        ) : null}
                        {event.recordLabel ? (
                          <Typography variant="caption" color="text.secondary">
                            Record: {event.recordLabel}
                          </Typography>
                        ) : null}
                        {event.resultingState ? (
                          <Typography variant="caption" color="text.secondary">
                            State: {event.resultingState}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Timeline events are not yet recorded on this summary.
              </Typography>
            )}
          </Box>

          <Box
            data-testid="risk-pattern-drawer-measurement"
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Measurement summary
            </Typography>
            {measurement?.emptyReason ? (
              <Typography variant="body2" color="text.secondary">
                {measurement.emptyReason}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1.25,
                }}
              >
                <MetaCell label="Baseline Scan" value={measurement?.baselineScan || 'Not recorded'} />
                <MetaCell label="Follow-up Scan" value={measurement?.followUpScan || 'Not recorded'} />
                <MetaCell
                  label="Coverage comparability"
                  value={measurement?.coverageComparability || 'Not recorded'}
                />
                <MetaCell
                  label="Measured difference"
                  value={measurement?.measuredDifference || 'Not recorded'}
                />
                <MetaCell
                  label="Interpretation"
                  value={measurement?.interpretationState || 'Not recorded'}
                />
                <MetaCell
                  label="Effectiveness review"
                  value={measurement?.effectivenessReviewLabel || 'Not recorded'}
                />
              </Box>
            )}
          </Box>

          <Divider />

          <Accordion
            disableGutters
            elevation={0}
            data-testid="risk-pattern-drawer-guidance"
            sx={{
              borderRadius: '8px !important',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Supporting guidance
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {guidanceBits.length > 0 ? (
                <Stack spacing={0.75}>
                  {guidanceBits.map((line) => (
                    <Typography key={line} variant="body2">{line}</Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Control guidance for this pattern is available in the full review when configured.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            elevation={0}
            data-testid="risk-pattern-drawer-methodology"
            sx={{
              borderRadius: '8px !important',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Methodology
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {methodologyBits.length > 0 ? (
                <Stack spacing={0.75}>
                  {methodologyBits.map((line) => (
                    <Typography key={line} variant="body2">{line}</Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  External alignment and taxonomy details stay collapsed here and do not drive the decision.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {technicalIds.length > 0 ? (
            <Accordion
              disableGutters
              elevation={0}
              data-testid="risk-pattern-drawer-technical"
              sx={{
                borderRadius: '8px !important',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Technical details
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={0.5}>
                  {technicalIds.map((entry) => (
                    <Typography key={`${entry.label}:${entry.value}`} variant="body2" color="text.secondary">
                      {entry.label}: {entry.value}
                    </Typography>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ) : null}
        </Stack>
      ) : null}
    </Drawer>
  );
}

export default RiskPatternAttentionDrawer;
